import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowDown,
  ArrowUp,
  Check,
  CircleDot,
  Lock,
  MoreHorizontal,
  Pencil,
  Plus,
  SlidersHorizontal,
  Trash2,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/atoms/dropdown-menu';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/atoms/sheet';
import { Switch } from '@/components/atoms/switch';
import { DetailPageLayout } from '@/components/layouts';
import {
  CollapsibleSectionHeader,
  DataTableDrawerHeader,
  DataTableSearchFilterField,
  DataTableSelectFilterField,
  FormAccordionCard,
  FormOptionalSectionDivider,
} from '@/components/molecules';
import { StatusChip } from '@/components/molecules/StatusChip';
import {
  DataTableFilterSection,
  SettingsDataGrid,
  type SettingsDataGridColumn,
  type SettingsDataGridSortDir,
} from '@/components/organisms';
import { Fullbleed } from '@/contexts/fullbleed';
import { useContactStatuses } from '@/hooks/contacts/useContactStatuses';
import { useApiMutation } from '@/hooks/useApiMutation';
import { useFilterDraft } from '@/hooks/useFilterDraft';
import { useHasPermission } from '@/hooks/usePermissions';
import { apiDelete, apiPatch, apiPost, operationsClient } from '@/lib/api';
import { contactStatusEndpoints } from '@/lib/api/endpoints';
import { contactKeys } from '@/lib/queryKeys';
import type { ContactStatus } from '@/types/contact';

type DrawerState = 'status-new' | 'status-edit' | 'status-detail';
type StatusColumn = 'status' | 'pipeline' | 'default' | 'active' | 'source' | 'order';
type PipelineFilter = 'all' | 'open' | 'closed';
type ActiveFilter = 'all' | 'active' | 'inactive';
type SourceFilter = 'all' | 'system' | 'custom';

interface ContactStatusesSearch {
  drawer?: DrawerState;
  statusId?: string;
}

interface ContactStatusFormValues {
  name: string;
  color: string;
  isDefault: boolean;
  isClosed: boolean;
  isActive: boolean;
}

interface CreateContactStatusPayload {
  name: string;
  color?: string;
  isDefault?: boolean;
  isClosed?: boolean;
}

interface UpdateContactStatusPayload extends CreateContactStatusPayload {
  isActive?: boolean;
}

interface ReorderContactStatusesPayload {
  statuses: Array<{ id: string; sortOrder: number }>;
}

export const Route = createFileRoute('/_dashboard/dashboard/settings/contact-statuses')({
  validateSearch: (search: Record<string, unknown>): ContactStatusesSearch => ({
    drawer:
      search.drawer === 'status-new' ||
      search.drawer === 'status-edit' ||
      search.drawer === 'status-detail'
        ? search.drawer
        : undefined,
    statusId: typeof search.statusId === 'string' ? search.statusId : undefined,
  }),
  component: ContactStatusesSettingsPage,
});

const PAGE_SIZE = 25;
const DEFAULT_FORM: ContactStatusFormValues = {
  name: '',
  color: '#2196F3',
  isDefault: false,
  isClosed: false,
  isActive: true,
};

const STATUS_COLUMNS: SettingsDataGridColumn<StatusColumn>[] = [
  { key: 'status', label: 'Status', sortable: true, width: 360 },
  { key: 'pipeline', label: 'Pipeline', sortable: true, width: 150 },
  { key: 'default', label: 'Default', sortable: true, align: 'center', width: 120 },
  { key: 'active', label: 'Active', sortable: true, align: 'center', width: 120 },
  { key: 'source', label: 'Source', sortable: true, width: 140 },
  { key: 'order', label: 'Order', sortable: true, align: 'center', width: 100 },
];

const PIPELINE_FILTER_OPTIONS: { value: PipelineFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'closed', label: 'Closed' },
];

const ACTIVE_FILTER_OPTIONS: { value: ActiveFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const SOURCE_FILTER_OPTIONS: { value: SourceFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'system', label: 'System' },
  { value: 'custom', label: 'Custom' },
];

// Deferred-apply drawer filters (NOT-391): the drawer selects edit a local draft;
// filtering only changes on Apply. Text search stays instant and is not drafted.
interface StatusFilterDraft {
  search: string;
  pipeline: PipelineFilter;
  active: ActiveFilter;
  source: SourceFilter;
}

const STATUS_FILTER_DEFAULTS: StatusFilterDraft = {
  search: '',
  pipeline: 'all',
  active: 'all',
  source: 'all',
};

function compareValues(a: string | number, b: string | number) {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b));
}

function normalizeColor(value: string) {
  const trimmed = value.trim();
  return isHexColor(trimmed) ? trimmed : DEFAULT_FORM.color;
}

function isHexColor(value: string) {
  return /^#[0-9A-Fa-f]{6}$/.test(value.trim());
}

function getSortValue(column: StatusColumn, status: ContactStatus) {
  switch (column) {
    case 'status':
      return status.name.toLowerCase();
    case 'pipeline':
      return status.isClosed ? 'closed' : 'open';
    case 'default':
      return status.isDefault ? 1 : 0;
    case 'active':
      return status.isActive ? 1 : 0;
    case 'source':
      return status.isSystem ? 'system' : 'custom';
    case 'order':
      return status.sortOrder;
  }
}

function ContactStatusesSettingsPage() {
  const router = useRouter();
  const searchParams = Route.useSearch();
  const canViewStatuses = useHasPermission('contacts:view');
  const canEditStatuses = useHasPermission('contacts:edit');
  const { data: statuses = [], isLoading } = useContactStatuses({ enabled: canViewStatuses });
  const createStatus = useCreateContactStatus();
  const updateStatus = useUpdateContactStatus();
  const deleteStatus = useDeleteContactStatus();
  const reorderStatuses = useReorderContactStatuses();

  const [search, setSearch] = useState('');
  const [pipelineFilter, setPipelineFilter] = useState<PipelineFilter>('all');
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('all');
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  const [sortKey, setSortKey] = useState<StatusColumn | null>('order');
  const [sortDir, setSortDir] = useState<SettingsDataGridSortDir>('asc');
  const [page, setPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState<ContactStatus | null>(null);

  const editStatus = useMemo(
    () => statuses.find((status) => status.id === searchParams.statusId) ?? null,
    [searchParams.statusId, statuses],
  );

  const filteredStatuses = useMemo(() => {
    const q = search.trim().toLowerCase();
    return statuses.filter((status) => {
      const matchesSearch =
        !q ||
        [status.name, status.isClosed ? 'closed' : 'open', status.isSystem ? 'system' : 'custom']
          .join(' ')
          .toLowerCase()
          .includes(q);
      const matchesPipeline =
        pipelineFilter === 'all' ||
        (pipelineFilter === 'closed' ? status.isClosed : !status.isClosed);
      const matchesActive =
        activeFilter === 'all' || (activeFilter === 'active' ? status.isActive : !status.isActive);
      const matchesSource =
        sourceFilter === 'all' || (sourceFilter === 'system' ? status.isSystem : !status.isSystem);

      return matchesSearch && matchesPipeline && matchesActive && matchesSource;
    });
  }, [activeFilter, pipelineFilter, search, sourceFilter, statuses]);

  const sortedStatuses = useMemo(() => {
    if (!sortKey) return filteredStatuses;
    return [...filteredStatuses].sort((a, b) => {
      const result = compareValues(getSortValue(sortKey, a), getSortValue(sortKey, b));
      return sortDir === 'asc' ? result : -result;
    });
  }, [filteredStatuses, sortDir, sortKey]);

  const orderedStatuses = useMemo(
    () => [...statuses].sort((a, b) => a.sortOrder - b.sortOrder),
    [statuses],
  );

  const activeFilterCount =
    (pipelineFilter !== 'all' ? 1 : 0) +
    (activeFilter !== 'all' ? 1 : 0) +
    (sourceFilter !== 'all' ? 1 : 0);
  const totalPages = Math.max(1, Math.ceil(sortedStatuses.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageStatuses = sortedStatuses.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const drawerMode =
    searchParams.drawer === 'status-new'
      ? 'create'
      : searchParams.drawer === 'status-detail'
        ? 'detail'
        : 'edit';
  const drawerOpen =
    searchParams.drawer === 'status-new' ||
    searchParams.drawer === 'status-edit' ||
    searchParams.drawer === 'status-detail';
  const drawerPending = createStatus.isPending || updateStatus.isPending;

  function openCreateDrawer() {
    if (!canEditStatuses) return;
    void router.navigate({
      to: '/dashboard/settings/contact-statuses',
      search: { drawer: 'status-new' },
    });
  }

  function openDetailDrawer(statusId: string) {
    void router.navigate({
      to: '/dashboard/settings/contact-statuses',
      search: { drawer: 'status-detail', statusId },
    });
  }

  function openEditDrawer(statusId: string) {
    if (!canEditStatuses) return;
    void router.navigate({
      to: '/dashboard/settings/contact-statuses',
      search: { drawer: 'status-edit', statusId },
    });
  }

  function closeDrawer() {
    void router.navigate({ to: '/dashboard/settings/contact-statuses', search: {} });
  }

  function resetFilters() {
    setSearch('');
    setPipelineFilter('all');
    setActiveFilter('all');
    setSourceFilter('all');
    setPage(1);
  }

  function handleSort(column: SettingsDataGridColumn<StatusColumn>, dir: SettingsDataGridSortDir) {
    if (!column.sortable) return;
    setSortKey(column.key);
    setSortDir(dir);
    setPage(1);
  }

  function moveStatus(status: ContactStatus, direction: 'up' | 'down') {
    if (!canEditStatuses) return;
    const currentIndex = orderedStatuses.findIndex((item) => item.id === status.id);
    const nextIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (currentIndex < 0 || nextIndex < 0 || nextIndex >= orderedStatuses.length) return;

    const nextStatuses = [...orderedStatuses];
    [nextStatuses[currentIndex], nextStatuses[nextIndex]] = [
      nextStatuses[nextIndex],
      nextStatuses[currentIndex],
    ];

    reorderStatuses.mutate({
      statuses: nextStatuses.map((item, index) => ({
        id: item.id,
        sortOrder: index + 1,
      })),
    });
  }

  function handleSave(values: ContactStatusFormValues) {
    if (!canEditStatuses) return;
    const name = values.name.trim();
    if (!name) {
      toast.error('Status name is required');
      return;
    }
    if (values.isDefault && values.isClosed) {
      toast.error('A status cannot be both default and closed');
      return;
    }
    if (!isHexColor(values.color)) {
      toast.error('Use a valid hex color');
      return;
    }

    const basePayload = {
      name,
      color: values.color.trim(),
      isDefault: values.isDefault,
      isClosed: values.isClosed,
    };

    if (drawerMode === 'edit' && editStatus) {
      updateStatus.mutate(
        {
          id: editStatus.id,
          dto: {
            ...basePayload,
            isActive: values.isDefault ? true : values.isActive,
          },
        },
        { onSuccess: closeDrawer },
      );
      return;
    }

    createStatus.mutate(basePayload, { onSuccess: closeDrawer });
  }

  function renderCell(key: StatusColumn, status: ContactStatus) {
    switch (key) {
      case 'status':
        return (
          <div className="flex min-w-0 items-center gap-3">
            <span
              className="h-3 w-3 shrink-0 rounded-full"
              style={{ backgroundColor: normalizeColor(status.color) }}
            />
            <div className="min-w-0">
              <div className="flex min-w-0 items-center gap-2">
                <p className="truncate text-sm font-medium">{status.name}</p>
                {status.isSystem ? <Lock className="h-3.5 w-3.5 text-muted-foreground" /> : null}
              </div>
              <p className="truncate text-xs text-muted-foreground">
                {status.isClosed ? 'Closed pipeline status' : 'Open pipeline status'}
              </p>
            </div>
          </div>
        );
      case 'pipeline':
        return <PipelineBadge status={status} />;
      case 'default':
        return <BoolIcon value={status.isDefault} />;
      case 'active':
        return <StatusActiveBadge status={status} />;
      case 'source':
        return status.isSystem ? (
          <Badge variant="secondary">System</Badge>
        ) : (
          <Badge variant="outline">Custom</Badge>
        );
      case 'order':
        return <span className="tabular-nums">{status.sortOrder}</span>;
    }
  }

  // Drawer selects write to a draft; committed pipeline/active/source still drive
  // the list and the activeFilterCount badge. Search stays instant (not drafted).
  const committedFilters = useMemo<StatusFilterDraft>(
    () => ({ search, pipeline: pipelineFilter, active: activeFilter, source: sourceFilter }),
    [search, pipelineFilter, activeFilter, sourceFilter],
  );
  const filterDraft = useFilterDraft(
    committedFilters,
    (next) => {
      setSearch(next.search);
      setPipelineFilter(next.pipeline);
      setActiveFilter(next.active);
      setSourceFilter(next.source);
      setPage(1);
    },
    STATUS_FILTER_DEFAULTS,
  );
  const { draft } = filterDraft;
  const draftFilterCount =
    (draft.search.trim() ? 1 : 0) +
    (draft.pipeline !== 'all' ? 1 : 0) +
    (draft.active !== 'all' ? 1 : 0) +
    (draft.source !== 'all' ? 1 : 0);

  const filterContent = (
    <>
      <DataTableFilterSection
        label="Status"
        active={!!draft.search}
        onClear={() => filterDraft.setDraft({ search: '' })}
      >
        <DataTableSearchFilterField
          value={draft.search}
          onValueChange={(value) => filterDraft.setDraft({ search: value })}
          placeholder="Search by name or type..."
        />
      </DataTableFilterSection>

      <DataTableFilterSection
        label="Pipeline"
        active={draft.pipeline !== 'all'}
        onClear={() => filterDraft.setDraft({ pipeline: 'all' })}
      >
        <DataTableSelectFilterField
          value={draft.pipeline}
          onValueChange={(value) => filterDraft.setDraft({ pipeline: value as PipelineFilter })}
          placeholder="All pipeline states"
          options={PIPELINE_FILTER_OPTIONS}
        />
      </DataTableFilterSection>

      <DataTableFilterSection
        label="Active"
        active={draft.active !== 'all'}
        onClear={() => filterDraft.setDraft({ active: 'all' })}
      >
        <DataTableSelectFilterField
          value={draft.active}
          onValueChange={(value) => filterDraft.setDraft({ active: value as ActiveFilter })}
          placeholder="All statuses"
          options={ACTIVE_FILTER_OPTIONS}
        />
      </DataTableFilterSection>

      <DataTableFilterSection
        label="Source"
        active={draft.source !== 'all'}
        onClear={() => filterDraft.setDraft({ source: 'all' })}
      >
        <DataTableSelectFilterField
          value={draft.source}
          onValueChange={(value) => filterDraft.setDraft({ source: value as SourceFilter })}
          placeholder="All sources"
          options={SOURCE_FILTER_OPTIONS}
        />
      </DataTableFilterSection>
    </>
  );

  return (
    <>
      <Fullbleed />
      <DetailPageLayout
        header={
          <>
            <div className="min-w-0">
              <h1 className="truncate text-base font-semibold">Contact Statuses</h1>
              <p className="text-xs text-muted-foreground">
                Manage contact pipeline statuses used across contacts and kanban views.
              </p>
            </div>
            {canEditStatuses ? (
              <Button size="sm" onClick={openCreateDrawer}>
                <Plus className="h-4 w-4" />
                Add status
              </Button>
            ) : null}
          </>
        }
        contentClassName="flex min-h-0 flex-col p-0"
      >
        {!canViewStatuses ? (
          <PermissionNotice message="You do not have permission to view contact statuses." />
        ) : (
          <SettingsDataGrid
            columns={STATUS_COLUMNS}
            rows={pageStatuses}
            getRowKey={(status) => status.id}
            renderCell={renderCell}
            storageKey="settings-contact-statuses"
            emptyIcon={CircleDot}
            emptyTitle={statuses.length === 0 ? 'No contact statuses yet' : 'No matching statuses'}
            searchValue={search}
            onSearchChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            searchPlaceholder="Search statuses..."
            showClearAll={Boolean(search) || activeFilterCount > 0}
            onClearAll={resetFilters}
            activeFilterCount={activeFilterCount}
            filterSheet={{
              title: 'Filters',
              onReset: filterDraft.reset,
              resetDisabled: draftFilterCount === 0,
              onApply: filterDraft.apply,
              onOpen: filterDraft.seed,
              content: filterContent,
            }}
            showExport={false}
            isLoading={isLoading}
            skeletonRowCount={8}
            onRowClick={(status) => openDetailDrawer(status.id)}
            rowClassName="cursor-pointer"
            renderRowActions={(status) => {
              const orderIndex = orderedStatuses.findIndex((item) => item.id === status.id);
              return (
                <ContactStatusRowActions
                  canEdit={canEditStatuses}
                  status={status}
                  canMoveUp={orderIndex > 0}
                  canMoveDown={orderIndex >= 0 && orderIndex < orderedStatuses.length - 1}
                  onEdit={() => openEditDrawer(status.id)}
                  onMoveUp={() => moveStatus(status, 'up')}
                  onMoveDown={() => moveStatus(status, 'down')}
                  onDelete={() => setDeleteTarget(status)}
                />
              );
            }}
            sortKey={sortKey}
            sortDir={sortDir}
            onSort={handleSort}
            pagination={{
              count: sortedStatuses.length,
              singular: 'status',
              plural: 'statuses',
              page: currentPage,
              totalPages,
              onPrevious: () => setPage((value) => Math.max(1, value - 1)),
              onNext: () => setPage((value) => Math.min(totalPages, value + 1)),
            }}
          />
        )}
      </DetailPageLayout>

      <ContactStatusDrawer
        open={drawerOpen}
        mode={drawerMode}
        status={editStatus}
        isLoadingStatus={searchParams.drawer === 'status-edit' && isLoading}
        isPending={drawerPending}
        onClose={closeDrawer}
        onSave={handleSave}
        onEdit={editStatus ? () => openEditDrawer(editStatus.id) : undefined}
        onBack={
          searchParams.drawer === 'status-edit' && editStatus
            ? () => openDetailDrawer(editStatus.id)
            : undefined
        }
      />

      <Dialog
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete contact status?</DialogTitle>
            <DialogDescription>
              {deleteTarget ? (
                <>
                  Delete <strong>{deleteTarget.name}</strong>? Contacts using this status may block
                  deletion until they are moved to another status.
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={deleteStatus.isPending}
              onClick={() => setDeleteTarget(null)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteStatus.isPending}
              onClick={() => {
                if (!deleteTarget) return;
                deleteStatus.mutate(deleteTarget.id, {
                  onSuccess: () => setDeleteTarget(null),
                });
              }}
            >
              {deleteStatus.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function PermissionNotice({ message }: { message: string }) {
  return (
    <div className="flex min-h-64 flex-1 items-center justify-center p-6 text-center">
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  );
}

function ContactStatusRowActions({
  canEdit,
  status,
  canMoveUp,
  canMoveDown,
  onEdit,
  onMoveUp,
  onMoveDown,
  onDelete,
}: {
  canEdit: boolean;
  status: ContactStatus;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onEdit: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
}) {
  if (!canEdit) return null;

  const deleteDisabled = status.isSystem || status.isDefault;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={onEdit}>
          <Pencil className="h-3.5 w-3.5" />
          Edit
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onMoveUp} disabled={!canMoveUp}>
          <ArrowUp className="h-3.5 w-3.5" />
          Move up
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onMoveDown} disabled={!canMoveDown}>
          <ArrowDown className="h-3.5 w-3.5" />
          Move down
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem variant="destructive" onSelect={onDelete} disabled={deleteDisabled}>
          <Trash2 className="h-3.5 w-3.5" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function StatusDetailContent({
  status,
  onEdit,
  onClose,
}: {
  status: ContactStatus;
  onEdit: () => void;
  onClose: () => void;
}) {
  return (
    <>
      <SheetHeader className="border-b px-5 py-4">
        <div className="flex items-center justify-between">
          <div className="flex min-w-0 items-center gap-3">
            <StatusAvatar status={status} />
            <SheetTitle className="text-sm font-semibold">{status.name}</SheetTitle>
          </div>
          <div className="flex items-center gap-2">
            <Button size="icon" variant="outline" className="h-8 w-8" title="Edit" onClick={onEdit}>
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      </SheetHeader>
      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5">
        <FormAccordionCard
          open
          header={
            <CollapsibleSectionHeader icon={CircleDot} label="Details" open onToggle={() => {}} />
          }
        >
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between gap-4">
              <span className="text-muted-foreground">Name</span>
              <span className="font-medium">{status.name}</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span className="text-muted-foreground">Color</span>
              <div className="flex items-center gap-2">
                <span
                  className="inline-block h-4 w-4 rounded-sm border"
                  style={{ backgroundColor: normalizeColor(status.color) }}
                />
                <span className="font-mono">{normalizeColor(status.color)}</span>
              </div>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span className="text-muted-foreground">Pipeline</span>
              <PipelineBadge status={status} />
            </div>
            <div className="flex items-center justify-between gap-4">
              <span className="text-muted-foreground">Default</span>
              <BoolIcon value={status.isDefault} />
            </div>
            <div className="flex items-center justify-between gap-4">
              <span className="text-muted-foreground">Active</span>
              <StatusActiveBadge status={status} />
            </div>
            <div className="flex items-center justify-between gap-4">
              <span className="text-muted-foreground">Source</span>
              {status.isSystem ? (
                <Badge variant="secondary">System</Badge>
              ) : (
                <Badge variant="outline">Custom</Badge>
              )}
            </div>
            <div className="flex items-center justify-between gap-4">
              <span className="text-muted-foreground">Order</span>
              <span className="tabular-nums">{status.sortOrder}</span>
            </div>
          </div>
        </FormAccordionCard>
      </div>
    </>
  );
}

function ContactStatusDrawer({
  open,
  mode,
  status,
  isLoadingStatus,
  isPending,
  onClose,
  onSave,
  onEdit,
  onBack,
}: {
  open: boolean;
  mode: 'create' | 'edit' | 'detail';
  status: ContactStatus | null;
  isLoadingStatus: boolean;
  isPending: boolean;
  onClose: () => void;
  onSave: (values: ContactStatusFormValues) => void;
  onEdit?: () => void;
  onBack?: () => void;
}) {
  const [form, setForm] = useState<ContactStatusFormValues>(DEFAULT_FORM);
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [behaviorOpen, setBehaviorOpen] = useState(false);
  const isEdit = mode === 'edit';
  const isFormValid =
    form.name.trim().length > 0 && isHexColor(form.color) && !(form.isDefault && form.isClosed);

  useEffect(() => {
    if (!open || mode === 'create') {
      setForm(DEFAULT_FORM);
      setDetailsOpen(true);
      setBehaviorOpen(false);
      return;
    }

    if (!status) return;

    setForm({
      name: status.name,
      color: normalizeColor(status.color),
      isDefault: status.isDefault,
      isClosed: status.isClosed,
      isActive: status.isActive,
    });
    setDetailsOpen(true);
    setBehaviorOpen(false);
  }, [mode, open, status]);

  function setDefault(nextValue: boolean) {
    setForm((current) => ({
      ...current,
      isDefault: nextValue,
      isClosed: nextValue ? false : current.isClosed,
      isActive: nextValue ? true : current.isActive,
    }));
  }

  function setClosed(nextValue: boolean) {
    setForm((current) => ({
      ...current,
      isClosed: nextValue,
      isDefault: nextValue ? false : current.isDefault,
    }));
  }

  function setActive(nextValue: boolean) {
    setForm((current) => ({
      ...current,
      isActive: nextValue,
      isDefault: nextValue ? current.isDefault : false,
    }));
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    onSave(form);
  }

  return (
    <Sheet
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-2xl"
        showCloseButton={false}
      >
        {mode === 'detail' && status ? (
          <StatusDetailContent status={status} onEdit={onEdit!} onClose={onClose} />
        ) : (
          <>
            <DataTableDrawerHeader
              title={isEdit ? 'Edit Contact Status' : 'Create Contact Status'}
              onClose={onClose}
              onBack={onBack}
            />

            <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
              <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5">
                {isLoadingStatus ? (
                  <p className="text-sm text-muted-foreground">Loading status...</p>
                ) : isEdit && !status ? (
                  <p className="text-sm text-muted-foreground">Contact status not found.</p>
                ) : (
                  <>
                    <FormAccordionCard
                      open={detailsOpen}
                      header={
                        <CollapsibleSectionHeader
                          icon={CircleDot}
                          label="Details"
                          badge="required"
                          open={detailsOpen}
                          onToggle={() => setDetailsOpen((current) => !current)}
                        />
                      }
                    >
                      <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_160px]">
                        <div className="space-y-2">
                          <Label htmlFor="contact-status-name">Name</Label>
                          <Input
                            id="contact-status-name"
                            value={form.name}
                            disabled={isPending}
                            onChange={(event) =>
                              setForm((current) => ({ ...current, name: event.target.value }))
                            }
                            placeholder="e.g. Active"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="contact-status-color">Color</Label>
                          <div className="flex items-center gap-2">
                            <Input
                              id="contact-status-color"
                              type="color"
                              value={normalizeColor(form.color)}
                              disabled={isPending}
                              onChange={(event) =>
                                setForm((current) => ({ ...current, color: event.target.value }))
                              }
                              className="h-10 w-12 shrink-0 p-1"
                            />
                            <Input
                              value={form.color}
                              disabled={isPending}
                              onChange={(event) =>
                                setForm((current) => ({ ...current, color: event.target.value }))
                              }
                              placeholder="#2196F3"
                              className="font-mono text-xs"
                            />
                          </div>
                        </div>
                      </div>
                    </FormAccordionCard>

                    <FormOptionalSectionDivider />

                    <FormAccordionCard
                      open={behaviorOpen}
                      header={
                        <CollapsibleSectionHeader
                          icon={SlidersHorizontal}
                          label="Behavior"
                          badge="optional"
                          open={behaviorOpen}
                          onToggle={() => setBehaviorOpen((current) => !current)}
                        />
                      }
                    >
                      <div className="space-y-3">
                        <SwitchSetting
                          label="Default status"
                          description="New contacts are assigned this status when no other status is selected."
                          checked={form.isDefault}
                          disabled={isPending}
                          onCheckedChange={setDefault}
                        />
                        <SwitchSetting
                          label="Closed status"
                          description="Use for inactive contacts or contacts that should leave the active pipeline."
                          checked={form.isClosed}
                          disabled={isPending}
                          onCheckedChange={setClosed}
                        />
                        <SwitchSetting
                          label="Active"
                          description="Inactive statuses stay available historically but are hidden from normal selection."
                          checked={form.isActive}
                          disabled={isPending}
                          onCheckedChange={setActive}
                        />
                      </div>
                    </FormAccordionCard>
                  </>
                )}
              </div>

              <SheetFooter className="flex-row justify-end border-t px-5 py-4">
                <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isPending || !isFormValid || (isEdit && !status)}>
                  {isPending ? (isEdit ? 'Saving...' : 'Creating...') : isEdit ? 'Save' : 'Create'}
                </Button>
              </SheetFooter>
            </form>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function SwitchSetting({
  label,
  description,
  checked,
  disabled,
  onCheckedChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  disabled: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-md border px-4 py-3">
      <div className="min-w-0">
        <p className="text-sm font-medium">{label}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <Switch checked={checked} disabled={disabled} onCheckedChange={onCheckedChange} />
    </div>
  );
}

function StatusAvatar({ status }: { status: ContactStatus }) {
  return (
    <div
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border text-sm font-medium"
      style={{
        backgroundColor: `${normalizeColor(status.color)}1A`,
        borderColor: `${normalizeColor(status.color)}66`,
        color: normalizeColor(status.color),
      }}
    >
      {status.name.trim().charAt(0).toUpperCase() || 'S'}
    </div>
  );
}

function PipelineBadge({ status }: { status: ContactStatus }) {
  return status.isClosed ? (
    <Badge variant="secondary">Closed</Badge>
  ) : (
    <Badge variant="outline" className="border-green-200 bg-green-50 text-green-700">
      Open
    </Badge>
  );
}

function StatusActiveBadge({ status }: { status: ContactStatus }) {
  return (
    <StatusChip
      status={status.isActive ? 'ACTIVE' : 'INACTIVE'}
      label={status.isActive ? 'Active' : 'Inactive'}
      variant="table"
    />
  );
}

function BoolIcon({ value }: { value: boolean }) {
  return value ? (
    <Check className="mx-auto h-4 w-4 text-green-600" />
  ) : (
    <X className="mx-auto h-4 w-4 text-muted-foreground/40" />
  );
}

function useCreateContactStatus() {
  const queryClient = useQueryClient();
  return useApiMutation<ContactStatus, CreateContactStatusPayload>({
    mutationFn: (dto) =>
      apiPost<ContactStatus, CreateContactStatusPayload>(
        operationsClient,
        contactStatusEndpoints.list(),
        dto,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: contactKeys.all() });
      toast.success('Contact status created');
    },
  });
}

function useUpdateContactStatus() {
  const queryClient = useQueryClient();
  return useApiMutation<ContactStatus, { id: string; dto: UpdateContactStatusPayload }>({
    mutationFn: ({ id, dto }) =>
      apiPatch<ContactStatus, UpdateContactStatusPayload>(
        operationsClient,
        contactStatusEndpoints.detail(id),
        dto,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: contactKeys.all() });
      toast.success('Contact status saved');
    },
  });
}

function useDeleteContactStatus() {
  const queryClient = useQueryClient();
  return useApiMutation<void, string>({
    mutationFn: (id) => apiDelete<void>(operationsClient, contactStatusEndpoints.detail(id)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: contactKeys.all() });
      toast.success('Contact status deleted');
    },
  });
}

function useReorderContactStatuses() {
  const queryClient = useQueryClient();
  return useApiMutation<ContactStatus[], ReorderContactStatusesPayload>({
    mutationFn: (payload) =>
      apiPost<ContactStatus[], ReorderContactStatusesPayload>(
        operationsClient,
        contactStatusEndpoints.reorder(),
        payload,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: contactKeys.all() });
      toast.success('Contact statuses reordered');
    },
  });
}
